<div class="max-w-4xl mx-auto pt-6 min-h-screen">
    <p-toast></p-toast>

    <div class="text-center mb-8">
        <h1 class="text-3xl font-semibold text-gray-800 mb-2" i18n>{{ isEditMode ? 'Edit User' : 'Add New User' }}</h1>
        <p class="text-gray-600 text-lg m-0" i18n>{{ isEditMode ? 'Update user information and permissions.' : 'Invite and manage users across your organization.' }}</p>
    </div>

    <!-- Show message if user can't invite users -->
    <div *ngIf="!canInviteUsers() && !isEditMode && !loading" class="mb-8">
        <p-card styleClass="bg-white rounded-xl shadow-lg border-0">
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-br from-orange-400 to-orange-500 text-white rounded-t-xl p-6">
                    <div class="flex items-center gap-3 text-xl font-medium">
                        <i class="pi pi-exclamation-triangle text-2xl"></i>
                        <span i18n>No Permission</span>
                    </div>
                </div>
            </ng-template>
            <div class="p-8 text-center">
                <p class="text-gray-600 mb-6 text-lg" i18n>You don't have permission to invite users. Only users with CSM, SM, SA, or A roles can invite new users.</p>
                <p-button appGlobalLoadingDisable i18n-label label="Go Back" icon="pi pi-arrow-left" styleClass="p-button-secondary" (onClick)="goBack()">
                </p-button>
            </div>
        </p-card>
    </div>

    <!-- Show message if user can't edit users -->
    <div *ngIf="!canEditUsers() && isEditMode && !loading" class="mb-8">
        <p-card styleClass="bg-white rounded-xl shadow-lg border-0">
            <ng-template pTemplate="header">
                <div class="bg-gradient-to-br from-orange-400 to-orange-500 text-white rounded-t-xl p-6">
                    <div class="flex items-center gap-3 text-xl font-medium">
                        <i class="pi pi-exclamation-triangle text-2xl"></i>
                        <span i18n>No Permission</span>
                    </div>
                </div>
            </ng-template>
            <div class="p-8 text-center">
                <p class="text-gray-600 mb-6 text-lg" i18n>You don't have permission to edit users. Only users with CSM, SM, SA, or A roles can edit users.</p>
                <p-button appGlobalLoadingDisable i18n-label label="Go Back" icon="pi pi-arrow-left" styleClass="p-button-secondary" (onClick)="goBack()">
                </p-button>
            </div>
        </p-card>
    </div>

    <!-- Show form only if user has appropriate permissions -->
    <form [formGroup]="userForm" (ngSubmit)="onSubmit()" class="space-y-6">
    <p-card *ngIf="(canInviteUsers() && !isEditMode) || (canEditUsers() && isEditMode)" styleClass="bg-white rounded-xl shadow-lg border-0 p-4">

        <!-- Back button for edit mode -->
        <div *ngIf="isEditMode" class="mb-4">
            <p-button appGlobalLoadingDisable i18n-label label="Back to User List" icon="pi pi-arrow-left" styleClass="p-button-secondary" (onClick)="goBack()">
            </p-button>
        </div>

        <!-- Warning if editing own account -->
        <div *ngIf="isEditingOwnAccount()" class="mb-4">
            <p-card styleClass="bg-yellow-50 border-yellow-200">
                <div class="flex items-center gap-3 text-yellow-800">
                    <i class="pi pi-exclamation-triangle text-xl"></i>
                    <div>
                        <p class="font-medium mb-1" i18n>Editing Your Own Account</p>
                        <p class="text-sm" i18n>You are currently editing your own user account. Changes will affect your own access and permissions.</p>
                    </div>
                </div>
            </p-card>
        </div>

        <div class="flex items-center gap-3 text-xl font-medium">
            <i class="pi pi-user text-2xl"></i>
            <span i18n>{{ isEditMode ? 'Edit User Information' : 'User Information' }}</span>
        </div>

        <div class="mt-6">
           
                <!-- Loading skeleton for basic form fields -->
                <div *ngIf="loading" class="space-y-6">
                    <!-- Email skeleton -->
                    <div class="flex flex-col gap-2">
                        <p-skeleton height="1.25rem" width="30%" borderRadius="0.25rem"></p-skeleton>
                        <p-skeleton height="3rem" borderRadius="0.5rem"></p-skeleton>
                        <p-skeleton height="1rem" width="50%" borderRadius="0.25rem"></p-skeleton>
                    </div>
                    
                    <!-- First/Last name skeletons -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="flex flex-col gap-2">
                            <p-skeleton height="1.25rem" width="40%" borderRadius="0.25rem"></p-skeleton>
                            <p-skeleton height="3rem" borderRadius="0.5rem"></p-skeleton>
                            <p-skeleton height="1rem" width="60%" borderRadius="0.25rem"></p-skeleton>
                        </div>
                        <div class="flex flex-col gap-2">
                            <p-skeleton height="1.25rem" width="40%" borderRadius="0.25rem"></p-skeleton>
                            <p-skeleton height="3rem" borderRadius="0.5rem"></p-skeleton>
                            <p-skeleton height="1rem" width="60%" borderRadius="0.25rem"></p-skeleton>
                        </div>
                    </div>
                    
                    <!-- Role skeleton -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div class="flex flex-col gap-2">
                            <p-skeleton height="1.25rem" width="35%" borderRadius="0.25rem"></p-skeleton>
                            <p-skeleton height="3rem" borderRadius="0.5rem"></p-skeleton>
                            <p-skeleton height="1rem" width="80%" borderRadius="0.25rem"></p-skeleton>
                        </div>
                    </div>
                </div>
                
                <!-- Actual form fields (shown when not loading) -->
                <div *ngIf="!loading">
                <div class="grid grid-cols-1 gap-6 mb-6">
                    <!-- Email Address -->
                    <div class="flex flex-col gap-2">
                        <label for="email" class="font-medium text-gray-800 mb-1" i18n>
                            Email Address <span class="text-red-500">*</span>
                            <span *ngIf="isEditMode" class="text-gray-500 text-sm ml-2">(Read-only in edit mode)</span>
                        </label>
                        <input id="email" type="email" pInputText formControlName="email" i18n-placeholder placeholder="your.email@example.com" class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" [class.ng-invalid]="userForm.get('email')?.invalid && userForm.get('email')?.touched" [readonly]="isEditMode" />
                        <small class="text-red-500 text-sm mt-0" *ngIf="getFieldError('email')">
                            {{ getFieldError('email') }}
                        </small>
                        <small *ngIf="isEditMode" class="text-gray-500 text-sm mt-0" i18n>
                            Email address cannot be changed as it's used for user identification.
                        </small>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <!-- First Name -->
                    <div class="flex flex-col gap-2">
                        <label for="firstName" class="font-medium text-gray-800 mb-1" i18n>
                            First Name <span class="text-red-500">*</span>
                        </label>
                        <input id="firstName" type="text" pInputText formControlName="firstName" i18n-placeholder placeholder="Enter first name" class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" [class.ng-invalid]="userForm.get('firstName')?.invalid && userForm.get('firstName')?.touched" />
                        <small class="text-red-500 text-sm mt-1" *ngIf="getFieldError('firstName')">
                            {{ getFieldError('firstName') }}
                        </small>
                        <small class="text-gray-500 text-sm mt-1" i18n>Minimum 2 characters required</small>
                    </div>

                    <!-- Last Name -->
                    <div class="flex flex-col gap-2">
                        <label for="lastName" class="font-medium text-gray-800 mb-1" i18n>
                            Last Name <span class="text-red-500">*</span>
                        </label>
                        <input id="lastName" type="text" pInputText formControlName="lastName" i18n-placeholder placeholder="Enter last name" class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" [class.ng-invalid]="userForm.get('lastName')?.invalid && userForm.get('lastName')?.touched" />
                        <small class="text-red-500 text-sm" *ngIf="getFieldError('lastName')">
                            {{ getFieldError('lastName') }}
                        </small>
                        <small class="text-gray-500 text-sm">Minimum 2 characters required</small>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <!-- User Role -->
                    <div class="flex flex-col gap-2">
                        <label for="roleId" class="font-medium text-gray-800 mb-1" i18n>
                            User Role <span class="text-red-500">*</span>
                            <span *ngIf="isEditMode && !isEditingUserSA()" class="text-blue-600 text-sm ml-2">(Editable)</span>
                            <span *ngIf="isEditMode && isEditingUserSA()" class="text-orange-600 text-sm ml-2">(Protected)</span>
                        </label>
                        
                        <!-- Loading skeleton for roles -->
                        <div *ngIf="loadingRoles" class="space-y-2">
                            <p-skeleton height="3rem" borderRadius="0.5rem"></p-skeleton>
                            <p-skeleton height="1rem" width="70%" borderRadius="0.25rem"></p-skeleton>
                        </div>
                        
                        <!-- Roles dropdown -->
                        <div *ngIf="!loadingRoles">
                            <p-select 
                                id="roleId" 
                                formControlName="roleId" 
                                [options]="roles" 
                                optionLabel="name" 
                                optionValue="_id" 
                                i18n-placeholder
                                placeholder="Select a user role" 
                                class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" 
                                [class.ng-invalid]="userForm.get('roleId')?.invalid && userForm.get('roleId')?.touched"
                                [class.opacity-60]="isEditMode && isEditingUserSA()"
                                [disabled]="isEditMode && isEditingUserSA()" 
                                (onChange)="onRoleChange($event)">
                                <ng-template pTemplate="option" let-role>
                                    <div class="flex justify-between items-center py-2">
                                        <span class="font-medium">{{ role.name }}</span>
                                        <span class="ml-1 0 text-sm">({{ role.identification }})</span>
                                    </div>
                                </ng-template>
                            </p-select>
                            <small class="text-red-500 text-sm" *ngIf="getFieldError('roleId')">
                                {{ getFieldError('roleId') }}
                            </small>
                            <small class="text-gray-500 text-sm" *ngIf="roles.length === 0 && !loadingRoles" i18n>
                                No roles available for your current permission level.
                            </small>
                            <small *ngIf="isEditMode && !isEditingUserSA()" class="text-blue-600 text-sm" i18n>
                                You can change the user's role. Changes will take effect immediately upon saving.
                            </small>
                            <small *ngIf="isEditMode && isEditingUserSA()" class="text-orange-600 text-sm" i18n>
                                <i class="pi pi-shield mr-1"></i>
                                SA (Subscriber Admin) roles cannot be changed for security and billing reasons.
                            </small>
                        </div>
                    </div>
                     <!-- Company Name (conditional for SA role) -->
                     <div class="flex flex-col gap-2" *ngIf="isSARoleSelected()">
                        <label for="companyName" class="font-medium text-gray-800 mb-1" i18n>
                            Company Name <span class="text-red-500">*</span>
                        </label>
                        <input id="companyName" type="text" pInputText formControlName="companyName" i18n-placeholder placeholder="Enter company name" class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-indigo-100" [class.ng-invalid]="userForm.get('companyName')?.invalid && userForm.get('companyName')?.touched" (input)="onCompanyNameChange()" />
                        <small class="text-red-500 text-sm" *ngIf="getFieldError('companyName')">
                            {{ getFieldError('companyName') }}
                        </small>
                        <small class="text-gray-500 text-sm" i18n>Minimum 5 characters required</small>
                    </div>

                    <!-- Auto-populated Subscriber Information -->
                    <div class="flex flex-col gap-2" *ngIf="isSubscriberIdAutoPopulated()">
                        <label class="font-medium text-gray-800 mb-1" i18n>
                            Subscriber <span class="text-green-600 text-sm">(Auto-assigned)</span>
                        </label>
                        <div class="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-base text-gray-700">
                            {{ getAutoPopulatedSubscriberName() }}
                        </div>
                        <small class="text-gray-500 text-sm" i18n>
                            This user will automatically be assigned to your subscriber organization.
                        </small>
                    </div>

                    <!-- Subscriber (conditional) -->
                    <!-- <div class="flex flex-col gap-2" *ngIf="subscribers.length > 0">
                        <label for="subscriberId" class="font-medium text-gray-800 mb-1">
                            Subscriber
                        </label>
                        <p-select id="subscriberId" formControlName="subscriberId" [options]="subscribers" optionLabel="companyName" optionValue="_id" placeholder="Select Subscriber" class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 invalid:border-red-500 invalid:focus:ring-red-100" [class.ng-invalid]="userForm.get('subscriberId')?.invalid && userForm.get('subscriberId')?.touched">
                        </p-select>
                            <small class="text-red-500 text-sm" *ngIf="getFieldError('subscriberId')">
                            {{ getFieldError('subscriberId') }}
                        </small>
                    </div> -->
                </div>

                <!-- Products and Territories Selection -->
                <div *ngIf="showProductsAndTerritories" class="space-y-8 mt-8 pt-8 border-t border-gray-200">
                    <!-- Products Section -->
                    <div *ngIf="products.length > 0 || loadingProducts" class="space-y-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-2">
                                <h3 class="text-lg font-semibold text-gray-800" i18n>Products Access</h3>
                                <i class="pi pi-spin pi-spinner text-sm text-blue-500" *ngIf="loadingProducts"></i>
                            </div>
                            <div class="flex items-center gap-2" *ngIf="!loadingProducts">
                                <p-checkbox 
                                    [binary]="true"
                                    [ngModel]="areAllProductsSelected()" 
                                    (ngModelChange)="onToggleAllProducts($event)"
                                    [ngModelOptions]="{standalone: true}"
                                    [disabled]="loadingProducts"
                                ></p-checkbox>
                                <label class="text-sm font-medium text-gray-700" i18n>Select All</label>
                            </div>
                        </div>
                        
                        <div *ngIf="loadingProducts" class="flex items-center justify-center py-12">
                            <div class="text-center">
                                <p-progressSpinner 
                                    [style]="{'width': '40px', 'height': '40px'}" 
                                    strokeWidth="4"
                                    fill="transparent"
                                    animationDuration="1s">
                                </p-progressSpinner>
                                <p class="mt-3 text-gray-600 text-sm font-medium" i18n>Loading products...</p>
                                <p class="text-xs text-gray-500" i18n>Please wait while we fetch available products</p>
                            </div>
                        </div>
                        
                        <div *ngIf="!loadingProducts" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div *ngFor="let product of products" class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                                <p-checkbox 
                                    [binary]="true"
                                    [ngModel]="isProductSelected(product._id)" 
                                    (ngModelChange)="onProductToggle(product._id, $event)"
                                    [ngModelOptions]="{standalone: true}"
                                ></p-checkbox>
                                <div>
                                    <div class="font-medium text-gray-900">{{ product.name }}</div>
                                    <div class="text-sm text-gray-500">{{ product.code }}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Territories Section -->
                    <div *ngIf="getRegions().length > 0 || loadingTerritories" class="space-y-4">
                        <div class="flex items-center gap-2 mb-4">
                            <h3 class="text-lg font-semibold text-gray-800" i18n>Territories Access</h3>
                            <i class="pi pi-spin pi-spinner text-sm text-blue-500" *ngIf="loadingTerritories"></i>
                        </div>
                        
                        <div *ngIf="loadingTerritories" class="flex items-center justify-center py-12">
                            <div class="text-center">
                                <p-progressSpinner 
                                    [style]="{'width': '40px', 'height': '40px'}" 
                                    strokeWidth="4"
                                    fill="transparent"
                                    animationDuration="1s">
                                </p-progressSpinner>
                                <p class="mt-3 text-gray-600 text-sm font-medium" i18n>Loading territories...</p>
                                <p class="text-xs text-gray-500" i18n>Please wait while we fetch available territories</p>
                            </div>
                        </div>
                        
                        <p-accordion *ngIf="!loadingTerritories" [value]="0" class="w-full">
                            <p-accordion-panel 
                                *ngFor="let region of getRegions(); let i = index" 
                                [value]="i"
                            >
                                <p-accordion-header>
                                    <div class="flex items-center justify-between w-full pr-4">
                                        <span class="font-medium">{{ region }}</span>
                                        <div class="flex items-center gap-2" (click)="$event.stopPropagation()">
                                            <p-checkbox 
                                                [binary]="true"
                                                [ngModel]="areAllTerritoriesInRegionSelected(region)" 
                                                (ngModelChange)="onToggleAllTerritoriesInRegion(region, $event)"
                                                [ngModelOptions]="{standalone: true}"
                                            ></p-checkbox>
                                            <span class="text-sm text-gray-600" i18n>Select All</span>
                                        </div>
                                    </div>
                                </p-accordion-header>
                                <p-accordion-content>
                                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
                                        <div 
                                            *ngFor="let territory of getTerritoriesForRegion(region)" 
                                            class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                                        >
                                            <p-checkbox 
                                                [binary]="true"
                                                [ngModel]="isTerritorySelected(territory._id)" 
                                                (ngModelChange)="onTerritoryToggle(territory._id, $event)"
                                                [ngModelOptions]="{standalone: true}"
                                            ></p-checkbox>
                                            <div>
                                                <span class="text-gray-900 font-medium">{{ territory.name }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </p-accordion-content>
                            </p-accordion-panel>
                        </p-accordion>
                    </div>
                </div>

                <!-- Bio Information Section (Only in Edit Mode) -->
                <div *ngIf="isEditMode" class="space-y-8 mt-8 pt-8 border-t border-gray-200">
                    <p-card styleClass="p-4">
                        <!-- Personal Information Section -->
                        <div class="">
                            <div class="flex items-center gap-3 mb-6">
                                <i class="pi pi-user text-xl text-gray-600"></i>
                                <h3 i18n>Personal Information</h3>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioTitle" class="font-medium text-gray-800 mb-1" i18n>
                                        Title (Optional)
                                    </label>
                                    <p-select 
                                        id="bioTitle"
                                        formControlName="title"
                                        [options]="titles"
                                        optionLabel="label"
                                        optionValue="value"
                                        i18n-placeholder
                                        placeholder="Mr." 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    >
                                    </p-select>
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="bioGender" class="font-medium text-gray-800 mb-1" i18n>
                                        Gender
                                    </label>
                                    <p-select 
                                        id="bioGender"
                                        formControlName="gender"
                                        [options]="genders"
                                        optionLabel="label"
                                        optionValue="value"
                                        i18n-placeholder
                                        placeholder="Select gender" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    >
                                    </p-select>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioMobileNumber" class="font-medium text-gray-800 mb-1" i18n>
                                        Telephone
                                    </label>
                                    <input 
                                        id="bioMobileNumber"
                                        type="tel" 
                                        pInputText 
                                        formControlName="mobileNumber"
                                        i18n-placeholder
                                        placeholder="+4477009000" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>
                            </div>
                        </div>
                    </p-card>

                    <p-card styleClass="p-4">
                        <!-- Residential Address Section -->
                        <div class="">
                            <div class="flex items-center gap-3 mb-6">
                                <i class="pi pi-home text-xl text-gray-600"></i>
                                <h3 i18n>Residential Address</h3>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioStreetAddress" class="font-medium text-gray-800 mb-1" i18n>
                                        Street Address
                                    </label>
                                    <input 
                                        id="bioStreetAddress"
                                        type="text" 
                                        pInputText 
                                        formControlName="streetAddress"
                                        i18n-placeholder
                                        placeholder="123 Main Street" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="bioHouseNumber" class="font-medium text-gray-800 mb-1" i18n>
                                        House Number
                                    </label>
                                    <input 
                                        id="bioHouseNumber"
                                        type="text" 
                                        pInputText 
                                        formControlName="houseNumber"
                                        i18n-placeholder
                                        placeholder="123A" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioPostcode" class="font-medium text-gray-800 mb-1" i18n>
                                        Postcode
                                    </label>
                                    <input 
                                        id="bioPostcode"
                                        type="text" 
                                        pInputText 
                                        formControlName="postcode"
                                        i18n-placeholder
                                        placeholder="SW1A 1AA" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="bioCity" class="font-medium text-gray-800 mb-1" i18n>
                                        City
                                    </label>
                                    <input 
                                        id="bioCity"
                                        type="text" 
                                        pInputText 
                                        formControlName="city"
                                        i18n-placeholder
                                        placeholder="London" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 gap-6 mt-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioCountry" class="font-medium text-gray-800 mb-1" i18n>
                                        Country
                                    </label>
                                    <p-select 
                                        id="bioCountry"
                                        formControlName="country"
                                        [options]="countries"
                                        optionLabel="name"
                                        optionValue="name"
                                        [filter]="true"
                                        filterBy="name"
                                        [showClear]="true"
                                        i18n-placeholder
                                        placeholder="Select a Country" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    >
                                        <ng-template let-country pTemplate="item">
                                            <div class="flex items-center gap-2">
                                                <div>{{ country.name }}</div>
                                            </div>
                                        </ng-template>
                                    </p-select>
                                </div>
                            </div>
                        </div>
                    </p-card>

                    <p-card styleClass="p-4">
                        <!-- Identification & Business Section -->
                        <div class="">
                            <div class="flex items-center gap-3 mb-6">
                                <i class="pi pi-id-card text-xl text-gray-600"></i>
                                <h3 i18n>Identification & Business</h3>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioCitizenship" class="font-medium text-gray-800 mb-1" i18n>
                                        Citizenship
                                    </label>
                                    <p-select 
                                        id="bioCitizenship"
                                        formControlName="citizenship"
                                        [options]="citizenships"
                                        optionLabel="name"
                                        optionValue="name"
                                        [filter]="true"
                                        filterBy="name"
                                        [showClear]="true"
                                        i18n-placeholder
                                        placeholder="Select a Citizenship" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    >
                                        <ng-template let-citizenship pTemplate="item">
                                            <div class="flex items-center gap-2">
                                                <div>{{ citizenship.name }}</div>
                                            </div>
                                        </ng-template>
                                    </p-select>
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="bioNationalInsuranceNo" class="font-medium text-gray-800 mb-1" i18n>
                                        National Insurance No.
                                    </label>
                                    <input 
                                        id="bioNationalInsuranceNo"
                                        type="text" 
                                        pInputText 
                                        formControlName="nationalInsuranceNo"
                                        i18n-placeholder
                                        placeholder="12 34 56 C" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>
                            </div>

                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioTaxNumber" class="font-medium text-gray-800 mb-1" i18n>
                                        Tax Number (Optional)
                                    </label>
                                    <input 
                                        id="bioTaxNumber"
                                        type="text" 
                                        pInputText 
                                        formControlName="taxNumber"
                                        i18n-placeholder
                                        placeholder="1234567890" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    />
                                </div>

                                <div class="flex flex-col gap-2">
                                    <label for="bioTradeLicence" class="font-medium text-gray-800 mb-1" i18n>
                                        Trade Licence
                                    </label>
                                    <p-select 
                                        id="bioTradeLicence"
                                        formControlName="tradeLicence"
                                        [options]="tradeLicences"
                                        optionLabel="label"
                                        optionValue="value"
                                        i18n-placeholder
                                        placeholder="Select option" 
                                        class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                    >
                                    </p-select>
                                </div>
                            </div>

                            <div class="grid grid-cols-1 gap-6 mt-6">
                                <div class="flex flex-col gap-2">
                                    <label for="bioIdCard" class="font-medium text-gray-800 mb-1" i18n>
                                        ID Card
                                    </label>
                                    
                                    <!-- File Upload Progress -->
                                    <div *ngIf="isFileUploading" class="mb-4">
                                        <p-progressBar mode="indeterminate" styleClass="h-2"></p-progressBar>
                                        <small class="text-gray-500 text-sm mt-2 block" i18n>Uploading file...</small>
                                    </div>

                                    <!-- Uploaded File Display -->
                                    <div *ngIf="uploadedFileUrl && !isFileUploading" class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                        <div class="flex items-center justify-between">
                                            <div class="flex items-center gap-3">
                                                <i class="pi pi-file text-green-600"></i>
                                                <span class="text-green-800 font-medium" i18n>File uploaded successfully</span>
                                            </div>
                                            <p-button appGlobalLoadingDisable 
                                                type="button" 
                                                icon="pi pi-times" 
                                                severity="secondary" 
                                                size="small"
                                                (onClick)="removeUploadedFile()"
                                                styleClass="p-button-text"
                                            ></p-button>
                                        </div>
                                        
                                        <!-- Show thumbnail only for image files -->
                                        <div *ngIf="uploadedFile && isImageFile(uploadedFile)" class="mt-4">
                                            <h5 class="text-sm font-medium text-gray-700 mb-2" i18n>Preview</h5>
                                            <div class="flex justify-center">
                                                <img [src]="uploadedFileUrl" alt="File preview" i18n-alt class="max-w-full h-auto max-h-48 object-contain border rounded-lg shadow-sm" />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Main File Upload with Drag & Drop -->
                                    <div 
                                        class="mb-4 p-4 border-2 border-dashed rounded-lg text-center drag-drop-area"
                                        [class]="dragOver ? 'border-blue-500 bg-blue-50 drag-over' : 'border-gray-300'"
                                        [class.loading]="isFileUploading"
                                        (dragover)="onDragOver($event)"
                                        (dragleave)="onDragLeave($event)"
                                        (drop)="onDrop($event)"
                                    >
                                        <input 
                                            type="file" 
                                            accept="application/pdf,image/png,image/jpeg,image/jpg"
                                            (change)="onFileUpload($event)"
                                            class="hidden"
                                            [disabled]="isFileUploading"
                                            id="file-input-idCard-add-edit" />
                                        
                                        <div class="flex flex-col items-center justify-center py-4">
                                            <svg class="mb-2" width="38" height="27" viewBox="0 0 38 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M8.6875 26.875C4.0293 26.875 0.25 23.0957 0.25 18.4375C0.25 14.7578 2.60547 11.6289 5.88672 10.4746C5.88086 10.3164 5.875 10.1582 5.875 10C5.875 4.82031 10.0703 0.625 15.25 0.625C18.7246 0.625 21.7539 2.51172 23.377 5.32422C24.2676 4.72656 25.3457 4.375 26.5 4.375C29.6055 4.375 32.125 6.89453 32.125 10C32.125 10.7148 31.9902 11.3945 31.75 12.0273C35.1719 12.7188 37.75 15.748 37.75 19.375C37.75 23.5176 34.3926 26.875 30.25 26.875H8.6875ZM13.3164 14.1602C12.7656 14.7109 12.7656 15.6016 13.3164 16.1465C13.8672 16.6914 14.7578 16.6973 15.3027 16.1465L17.5879 13.8613V21.7188C17.5879 22.498 18.2148 23.125 18.9941 23.125C19.7734 23.125 20.4004 22.498 20.4004 21.7188V13.8613L22.6855 16.1465C23.2363 16.6973 24.127 16.6973 24.6719 16.1465C25.2168 15.5957 25.2227 14.7051 24.6719 14.1602L19.9844 9.47266C19.4336 8.92188 18.543 8.92188 17.998 9.47266L13.3105 14.1602H13.3164Z" fill="#9CA3AF"/>
                                            </svg>
                                            <label 
                                                for="file-input-idCard-add-edit" 
                                                class="cursor-pointer text-blue-600 hover:text-blue-800 font-medium"
                                                [class.opacity-50]="isFileUploading"
                                                [class.cursor-not-allowed]="isFileUploading">
                                                {{ isFileUploading ? 'Uploading...' : 'Click to upload or drag and drop' }}
                                            </label>
                                            <p class="text-sm text-gray-500 mt-2" i18n>
                                                PDF, PNG, JPG (MAX. 10MB)
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <!-- Fallback text input for manual URL entry -->
                                    <div class="mt-4">
                                        <label for="bioIdCardUrl" class="font-medium text-gray-800 mb-1 text-sm" i18n>
                                            Or enter ID Card URL manually (Optional)
                                        </label>
                                        <input 
                                            id="bioIdCardUrl"
                                            type="text" 
                                            pInputText 
                                            formControlName="idCard"
                                            i18n-placeholder
                                            placeholder="ID Card URL" 
                                            class="w-full rounded-lg border border-gray-300 text-base transition-all duration-200 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100"
                                        />
                                        <small class="text-gray-500 text-sm" i18n>Enter the ID card file URL if you have it</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </p-card>
                </div>

                <!-- Submit Button -->
                
            </div>
        </div>
        </p-card>
        <div class="flex justify-end mt-4">
            <!-- <div *ngIf="isEditMode">
                <p-button appGlobalLoadingDisable label="Cancel" icon="pi pi-times" styleClass="p-button-secondary" (onClick)="goBack()">
                </p-button>
            </div> -->
            <div class="flex gap-3">
                <p-button 
                    type="submit" 
                    i18n-label
                    [label]="submitting ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update User' : 'Add User')" 
                    [loading]="submitting" 
                    [disabled]="!isFormValidForSubmission() || submitting || roles.length === 0 || loading || loadingRoles" 
                    styleClass="p-button-primary"
                    [icon]="submitting ? 'pi pi-spin pi-spinner' : (isEditMode ? 'pi pi-save' : 'pi pi-plus')">
                </p-button>
            </div>
        </div>
    </form>

    <!-- Enhanced Loading Spinner -->
    <div class="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50" *ngIf="loading">
        <div class="text-center bg-white p-10 rounded-2xl shadow-2xl border border-gray-100">
            <div class="mb-6">
                <p-progressSpinner 
                    [style]="{'width': '60px', 'height': '60px'}" 
                    styleClass="custom-spinner"
                    strokeWidth="3"
                    fill="transparent"
                    animationDuration="1s">
                </p-progressSpinner>
            </div>
            <div class="space-y-2">
                <p class="text-lg font-semibold text-gray-800 m-0" i18n>
                    {{ isEditMode ? 'Loading User Data...' : 'Setting Up Form...' }}
                </p>
                <p class="text-sm text-gray-600 m-0" i18n>
                    Please wait while we prepare everything for you
                </p>
            </div>
            <!-- Loading progress indicators -->
            <div class="mt-6 space-y-2">
                <div class="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <i class="pi pi-check text-green-500" *ngIf="!loadingRoles"></i>
                    <i class="pi pi-spin pi-spinner text-blue-500" *ngIf="loadingRoles"></i>
                    <span i18n>Roles</span>
                </div>
                <div class="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <i class="pi pi-check text-green-500" *ngIf="!loadingSubscribers"></i>
                    <i class="pi pi-spin pi-spinner text-blue-500" *ngIf="loadingSubscribers"></i>
                    <span i18n>Subscribers</span>
                </div>
                <div class="flex items-center justify-center gap-2 text-xs text-gray-500" *ngIf="isEditMode">
                    <i class="pi pi-check text-green-500"></i>
                    <span i18n>User Information</span>
                </div>
            </div>
        </div>
    </div>
</div>